import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sector, Site } from 'graphql-common';
import Typography from '@lib/components/Typography/Typography';
import Search from '@lib/components/Search/Search';
import Table from '@lib/components/Table/Table';
import EmptyState from '@lib/components/EmptyState/EmptyState';
import { IconName } from '@lib/components/Modal/enums';
import {
  GetUrlParams,
  ORDERING_DIRECTION_ASC,
  ORDERING_PARAMS,
  OrderingParams,
  PAGINATION_PARAMS,
  PaginationParams,
} from '@lib/enums/urls';
import { Column } from '@lib/components/Table/types';
import NewBadge from '../../NewBadge';
import styles from './PerformanceWidget.module.scss';

const defaultPaginationState = {
  ...PAGINATION_PARAMS,
  [GetUrlParams.OrderingField]: 'name',
  [GetUrlParams.OrderingDirection]: ORDERING_DIRECTION_ASC,
};

export type PerformanceWidgetData = Site | Sector;

export interface PerformanceWidgetProps {
  title: string;
  showBadge?: boolean;
  searchPlaceholder: string;
  data: PerformanceWidgetData[];
  totalCount: number;
  loading: boolean;
  error?: Error | null;
  columns: Column<PerformanceWidgetData>[];
  onSearchChange: (searchQuery: string) => void;
  paginationState: [
    PaginationParams,
    React.Dispatch<React.SetStateAction<PaginationParams>>,
  ];
  orderingState: [
    OrderingParams,
    React.Dispatch<React.SetStateAction<OrderingParams>>,
  ];
}

export default function PerformanceWidget({
  title,
  showBadge = false,
  searchPlaceholder,
  data,
  totalCount,
  loading,
  error,
  columns,
  onSearchChange,
  paginationState,
  orderingState,
}: PerformanceWidgetProps) {
  const { t } = useTranslation();

  const hasError = !!error;

  return (
    <div className={styles.container}>
      {showBadge && <NewBadge className={styles.badge} />}
      <div className={styles.header}>
        <Typography variant="h3" className={styles.title}>
          {title}
        </Typography>
      </div>
      <div className={styles.searchWrapper}>
        <Search
          placeholder={searchPlaceholder}
          onChange={onSearchChange}
          useSearchParam={false}
          useBottomMargin={false}
        />
      </div>
      <div className={styles.tableWrapper}>
        {hasError ? (
          <EmptyState
            icon={IconName.Cancel}
            text={t('refresh-or-retry-later')}
            title={t('failed-to-load')}
          />
        ) : (
          <Table
            columns={columns}
            data={data}
            id="performance-widget"
            loading={loading}
            orderingState={orderingState}
            paginationState={paginationState}
            totalEntries={totalCount}
          />
        )}
      </div>
    </div>
  );
}

export { defaultPaginationState, ORDERING_PARAMS };
